import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getQueue,
  approveDocument,
  releaseDocument,
  rejectDocument,
} from "../api/documents";
import AdminNav from "../components/AdminNav";
import AsyncButton from "../components/AsyncButton";
import StatusMessage from "../components/StatusMessage";
import { getErrorMessage } from "../utils/apiErrors";

const statuses = ["submitted", "approved", "released", "rejected"];

const statusLabels = {
  submitted: "Submitted",
  approved: "Ready for Release",
  released: "Delivered to Inmate",
  rejected: "Rejected",
};

const scanStatusLabel = (status) => ({ pending: "Security scan pending", scanning: "Scanning", clean: "Security scan complete", bypassed: "Legacy security exception", suspicious: "Blocked by security controls", infected: "Blocked by security controls", failed: "Security scan unavailable" }[status] || "Security scan pending");

function statusClass(status) {
  if (status === "approved") {
    return "status-pill status-approved";
  }

  if (status === "released") {
    return "status-pill status-released";
  }

  if (status === "rejected") {
    return "status-pill status-rejected";
  }

  return "status-pill status-submitted";
}

export default function AdminDashboard() {
  const [status, setStatus] = useState("submitted");
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rejectingId, setRejectingId] = useState(null);
  const [reason, setReason] = useState("");
  const [actionId, setActionId] = useState(null);
  const [error, setError] = useState("");
  const [securityReviewEnforced, setSecurityReviewEnforced] = useState(false);

  const loadQueue = useCallback(async () => {
    setLoading(true);

    try {
      const res = await getQueue(status);
      setDocuments(res.data.data || []);
      setSecurityReviewEnforced(Boolean(res.data.security_review_enforced));
    } catch (err) {
      setError(getErrorMessage(err, "Unable to load the privileged mail queue."));
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  const counts = useMemo(() => {
    return {
      current: documents.length,
    };
  }, [documents]);

  const handleVerify = async (id) => {
    const confirmed = await window.confirm("Approve this document for release after verifying its source, recipient, and authorization metadata?");
    if (!confirmed || actionId) return;
    setActionId(id);
    setError("");
    try {
      await approveDocument(id);
      await loadQueue();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setActionId(null);
    }
  };

  const handleRelease = async (id) => {
    const confirmed = await window.confirm(
      "Release this privileged legal document to the inmate? This action confirms facility processing without viewing privileged content."
    );

    if (!confirmed || actionId) return;
    setActionId(id);
    setError("");
    try {
      await releaseDocument(id);
      await loadQueue();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setActionId(null);
    }
  };

  const handleReject = async () => {
    if (!reason.trim()) {
      alert("Enter rejection reason");
      return;
    }

    if (actionId) return;
    setActionId(rejectingId);
    setError("");
    try {
      await rejectDocument(rejectingId, reason.trim());
      setRejectingId(null);
      setReason("");
      await loadQueue();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setActionId(null);
    }
  };

  return (
    <div className="app-shell">
      <div className="topbar d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-3">
          <div className="brand-mark">CV</div>

          <div>
            <h5 className="mb-0">CounselVault</h5>
            <div className="small opacity-75">Privileged Mail Intake</div>
          </div>
        </div>
       <AdminNav />
      </div>

      <main className="page-wrap">
        <StatusMessage type="warning">{error}</StatusMessage>
        <div className="d-flex justify-content-between align-items-end mb-4">
          <div>
            <h2 className="mb-1">Privileged Mail Intake</h2>
            <p className="text-muted mb-0">
              Verify attorney/client authorization and control delivery without exposing privileged content.
            </p>
          </div>

          <div className="stat-card px-4 py-3 text-end">
            <div className="text-muted small">Current View</div>
            <h3 className="mb-0">{counts.current}</h3>
          </div>
        </div>

        <div className="alert alert-light border mb-4">
          <strong>Privilege protection:</strong>{" "}
          Facility actions verify source, recipient, and delivery status. Document contents are not shown in this queue.
        </div>

        <div className="portal-card mb-4">
          <div className="portal-card-body">
            <div className="d-flex gap-2 flex-wrap">
              {statuses.map((s) => (
                <button
                  key={s}
                  className={`btn ${
                    status === s ? "btn-dark" : "btn-outline-dark"
                  }`}
                  onClick={() => setStatus(s)}
                >
                  {statusLabels[s]}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="portal-card">
          <div className="portal-card-header d-flex justify-content-between align-items-center">
            <div>
              <h5 className="mb-0">{statusLabels[status]}</h5>

              <div className="text-muted small">
                Showing privileged legal mail assigned to your facility.
              </div>
            </div>

            <button className="btn btn-sm btn-outline-secondary" onClick={loadQueue}>
              Refresh
            </button>
          </div>

          <div className="portal-card-body">
            {loading ? (
              <p className="mb-0">Loading privileged mail...</p>
            ) : documents.length === 0 ? (
              <div className="text-center py-5">
                <h5>No records found</h5>
                <p className="text-muted mb-0">
                  Nothing is currently in this status.
                </p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Document</th>
                      <th>Attorney</th>
                      <th>Client / Inmate</th>
                      <th>Facility</th>
                      <th>Status</th>
                      <th>Security review</th>
                      <th>Submitted</th>
                      <th className="text-end">Processing</th>
                    </tr>
                  </thead>

                  <tbody>
                    {documents.map((doc) => {
                      const attorney = doc.attorney_profile?.user;
                      const lawFirm = doc.attorney_profile?.law_firm;
                      const inmate = doc.inmate;
                      const facility = doc.facility;

                      return (
                        <tr key={doc.id}>
                          <td>
                            <strong>{doc.title || "Privileged Legal Document"}</strong>

                            <div className="text-muted small">
                              {doc.original_filename || `Document ID #${doc.id}`}
                            </div>
                          </td>

                          <td>
                            <strong>{attorney?.name || "Attorney"}</strong>

                            <div className="text-muted small">
                              {lawFirm?.name || attorney?.email || "-"}
                            </div>
                          </td>

                          <td>
                            <strong>
                              {inmate
                                ? `${inmate.first_name} ${inmate.last_name}`
                                : "Inmate"}
                            </strong>

                            <div className="text-muted small">
                              {inmate?.inmate_number
                                ? `Inmate #${inmate.inmate_number}`
                                : "-"}
                            </div>
                          </td>

                          <td>{facility?.name || "Assigned facility"}</td>

                          <td>
                            <span className={statusClass(doc.status)}>
                              {statusLabels[doc.status] || doc.status}
                            </span>
                          </td>

                          <td className="small">
                            {securityReviewEnforced ? scanStatusLabel(doc.malware_scan_status) : "Malware-scan enforcement disabled"}
                          </td>

                          <td className="text-muted small">
                            {doc.created_at || "-"}
                          </td>

                          <td className="text-end">
                            <div className="btn-group">
                              {doc.status === "submitted" && (
                                <>
                                  <AsyncButton
                                    className="btn btn-sm btn-success"
                                    busy={actionId === doc.id}
                                    disabled={actionId !== null || (securityReviewEnforced && !["clean", "bypassed"].includes(doc.malware_scan_status))}
                                    busyText="Approving…"
                                    onClick={() => handleVerify(doc.id)}
                                  >
                                    Approve
                                  </AsyncButton>

                                  <button
                                    className="btn btn-sm btn-outline-danger"
                                    disabled={actionId !== null}
                                    onClick={() => setRejectingId(doc.id)}
                                  >
                                    Reject
                                  </button>
                                </>
                              )}

                              {doc.status === "approved" && (
                                <AsyncButton
                                  className="btn btn-sm btn-primary"
                                  busy={actionId === doc.id}
                                  disabled={actionId !== null || (securityReviewEnforced && !["clean", "bypassed"].includes(doc.malware_scan_status))}
                                  busyText="Releasing…"
                                  onClick={() => handleRelease(doc.id)}
                                >
                                  Release to Inmate
                                </AsyncButton>
                              )}

                              {doc.status === "released" && (
                                <span className="text-muted small">
                                  Delivery complete
                                </span>
                              )}

                              {doc.status === "rejected" && (
                                <span className="text-muted small">
                                  Rejected
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>

      {rejectingId && (
        <div
          className="modal d-block"
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
          aria-labelledby="reject-document-title"
          style={{ background: "rgba(0,0,0,0.45)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content border-0 shadow">
              <div className="modal-header">
                <h5 className="modal-title" id="reject-document-title">Reject Privileged Mail</h5>

                <button
                  className="btn-close"
                  aria-label="Close rejection dialog"
                  onClick={() => setRejectingId(null)}
                />
              </div>

              <div className="modal-body">
                <p className="text-muted small">
                  Rejection should be based on facility legal mail policy, source verification, recipient mismatch, or operational handling requirements — not privileged content review.
                </p>

                <label className="form-label" htmlFor="rejection-reason">Rejection reason</label>

                <textarea
                  className="form-control"
                  id="rejection-reason"
                  rows="4"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Example: Unable to verify recipient or attorney/client access."
                  autoFocus
                />
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setRejectingId(null)}
                >
                  Cancel
                </button>

                <AsyncButton className="btn btn-danger" busy={actionId === rejectingId} busyText="Rejecting…" onClick={handleReject}>
                  Reject Mail
                </AsyncButton>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
